import { useTranslation } from 'react-i18next';
import { Box, Button, Chip, Typography } from '@gapartners/ui';
import { api } from '@/store/api';
import type { CaseHistoryEntry } from '@/types/case';

interface CaseHistoryRowProps {
  entry: CaseHistoryEntry;
  contactId?: string;
  onOpenCaseId?: (caseId: number, contactId: string) => void;
  /** When true, badges + timestamp use fixed widths to align with column headers. */
  showColumns?: boolean;
}

const CREATED_COLORS = {
  bar: 'success.main',
  badgeBg: 'success.light',
  badgeColor: 'success.dark',
} as const;

const CLOSED_COLORS = {
  bar: 'error.main',
  badgeBg: 'error.light',
  badgeColor: 'error.dark',
} as const;

const REPLACED_COLORS = {
  badgeBg: 'warning.light',
  badgeColor: 'warning.dark',
} as const;

export function CaseHistoryRow({
  entry,
  contactId,
  onOpenCaseId,
  showColumns = false,
}: CaseHistoryRowProps) {
  const { t } = useTranslation();
  const isClosed = entry.action === 'closed';
  const palette = isClosed ? CLOSED_COLORS : CREATED_COLORS;

  const { data: customerData } = api.useGetCustomerInformationQuery(
    { workSpaceID: String(entry.workspaceId) },
    { skip: !entry.workspaceId }
  );

  const companyLabel =
    customerData?.companyName ?? `Workspace ${entry.workspaceId}`;

  const time = new Date(entry.timestamp).toLocaleString([], {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        py: 1.25,
        px: 1.5,
        borderBottom: '1px solid',
        borderColor: 'divider',
        '&:last-child': { borderBottom: 'none' },
        transition: 'background-color 0.15s',
        '&:hover': { bgcolor: 'action.hover' },
      }}
    >
      {/* Left accent bar */}
      <Box
        sx={{
          width: 4,
          alignSelf: 'stretch',
          borderRadius: 2,
          bgcolor: palette.bar,
          flexShrink: 0,
          minHeight: 36,
        }}
      />

      {/* Case ID + Company name */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        {onOpenCaseId && contactId ? (
          <Button
            variant="text"
            size="small"
            color="primary"
            onClick={() => onOpenCaseId(entry.caseId, contactId)}
            sx={{
              p: 0,
              minWidth: 0,
              textTransform: 'none',
              fontWeight: 700,
              fontSize: '0.8125rem',
              lineHeight: 1.3,
              color: 'primary.main',
            }}
          >
            #{entry.caseId}
          </Button>
        ) : (
          <Typography
            variant="body2"
            fontWeight={700}
            color="primary.main"
            sx={{ fontSize: '0.8125rem', lineHeight: 1.3 }}
          >
            #{entry.caseId}
          </Typography>
        )}
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: 'block', lineHeight: 1.3, mt: 0.125 }}
        >
          {companyLabel}
        </Typography>
      </Box>

      {/* Badges group */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.75,
          flexShrink: 0,
          ...(showColumns && { width: 200, justifyContent: 'center' }),
        }}
      >
        {/* Status badge */}
        {entry.status && (
          <Chip
            label={entry.status}
            size="small"
            sx={{
              height: 22,
              fontSize: '0.6875rem',
              fontWeight: 600,
              bgcolor: palette.badgeBg,
              color: palette.badgeColor,
              px: 0.5,
            }}
          />
        )}

        {/* Action badge */}
        <Chip
          label={
            isClosed
              ? t('components.contactCenter.closed')
              : t('components.contactCenter.created')
          }
          size="small"
          variant="outlined"
          sx={{
            height: 22,
            fontSize: '0.6875rem',
            fontWeight: 500,
            borderColor: palette.bar,
            color: palette.bar,
            px: 0.5,
          }}
        />

        {/* Replaced by */}
        {entry.replacedByCaseId != null && (
          <Chip
            label={`→ #${entry.replacedByCaseId}`}
            size="small"
            sx={{
              height: 22,
              fontSize: '0.6875rem',
              fontWeight: 600,
              bgcolor: REPLACED_COLORS.badgeBg,
              color: REPLACED_COLORS.badgeColor,
              px: 0.5,
            }}
          />
        )}
      </Box>

      {/* Timestamp */}
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{
          whiteSpace: 'nowrap',
          fontSize: '0.6875rem',
          flexShrink: 0,
          ...(showColumns && { width: 95, textAlign: 'right' }),
        }}
      >
        {time}
      </Typography>
    </Box>
  );
}
